import type {Config as GRPCConfig} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/config.types';
import {useMutation} from '../react-query';
import type {InferResponseType, MakeAllKeysRequired} from '../types';
import {
  useGetConsideredAbTestsFromGrpcTrailers,
  useGrpcClientConfig,
  useSetAbTestsFromTrailers,
} from './GrpcClientContext';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {
  GenericGrpcRequestCallbackOptions,
  GrpcQueryConfig,
  GrpcRequestServiceMethod,
  useGenericGrpcRequestFn,
} from './useGenericGrpcRequest';

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export interface GenericTadaridaConfig {
  config?: DeepPartial<GRPCConfig>;
}

export function useGrpcRequestMutation<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof GenericTadaridaConfig>
  > &
    GenericGrpcRequestCallbackOptions,
  ResponseType extends InferResponseType<
    ReturnType<ServiceMethod>
  > = InferResponseType<ReturnType<ServiceMethod>>,
>(
  maybeAsyncService: ServiceMethod,
  config: GrpcQueryConfig<ResponseType> = {},
) {
  const genericConfig = useGrpcClientConfig();
  const fetch = useGenericGrpcRequestFn(maybeAsyncService, {}, {
    config: genericConfig,
  } as GenericTadaridaConfig);

  const mutate = (params: ParamsType) =>
    fetch(convertAllNullPropertiesToUndefined<ParamsType>(params));

  const addAbTestsFromTrailers = useSetAbTestsFromTrailers();
  const getConsideredAbTestsFromGrpcTrailers =
    useGetConsideredAbTestsFromGrpcTrailers();

  return useMutation<ResponseType, unknown, ParamsType, unknown>(mutate, {
    ...config,
    onSuccess: result => {
      const consideredAbTestsFromGrpcTrailers =
        getConsideredAbTestsFromGrpcTrailers(result?.trailers);

      if (consideredAbTestsFromGrpcTrailers.length) {
        addAbTestsFromTrailers(consideredAbTestsFromGrpcTrailers);
      }

      config.onSuccess?.(result);
    },
  });
}
